import { useQuery } from '@tanstack/react-query';
import type { UserEventType } from '@chatsdet/user-events';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

/**
 * One org activity event, enriched server-side with the actor's display name +
 * email (left join to `users`). Feeds the dashboard "Recent activity" timeline.
 */
export interface OrgEvent {
  id: string;
  orgId: string;
  eventType: UserEventType;
  origin: string;
  metadata: Record<string, unknown>;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  createdAt: string;
}

type OrgEventsResponse = {
  ok: boolean;
  data: OrgEvent[];
  total: number;
  limit: number;
  offset: number;
};

/**
 * Fetch the most recent org activity events, filtered to a curated allowlist of
 * event types (passed as a comma-separated `eventType` param — the server reads
 * it via `inArray`). Keeps the timeline focused instead of drowning in noisy
 * `command.*` / `workflow.node-added` events.
 */
export function useOrgEvents(eventTypes: readonly UserEventType[], limit = 20) {
  const typesParam = eventTypes.join(',');
  return useQuery({
    queryKey: ['org-events', typesParam, limit],
    queryFn: async (): Promise<OrgEvent[]> => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (typesParam) params.set('eventType', typesParam);
      const res = await fetch(`${API_URL}/org/events?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load activity');
      const body = (await res.json()) as OrgEventsResponse;
      if (!body.ok) throw new Error('Org events response was not ok');
      return body.data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
